/**
 * costing — what a set costs at each BQC, read off the workbook's COGM sheet.
 *
 * THE SHEET IS THE RATE CARD. `COGM` in BQC1 to BQC6.xlsm carries one row per
 * parent SKU (TOP SKU: SS028, SS032, …) with its CATEGORY and the cost of a
 * set AT each stage — BQC1 through BQC5, cumulative, so BQC5 is the finished
 * garment's COGM. That is the figure the floor quotes and the finance side
 * reconciles against, and it lives in the same file the output rows come
 * from, which is why pricing happens at import: rates and rows are read
 * together, from one workbook, and a re-import reprices everything.
 *
 * TWO LOOKUPS, IN ORDER. A row is priced by its PARENT SKU first — the DESIGN
 * column where the sheet has one, the head of the SKU CODE ("SS028-MDBLU-L"
 * → SS028) where it does not. A parent the COGM sheet does not list falls
 * back to the AVERAGE of its CATEGORY's rows on that same sheet, and the
 * category comes from the OPERATIONS Masterfile (SKU CODE → category, or the
 * majority category of every SKU sharing the parent), then the row's own
 * CATEGORY cell. Neither found prices nothing: an unpriced set is COUNTED,
 * never guessed at the house mean, because a mean that reads like a
 * measurement is how the question stops being asked.
 *
 * Zero-import but for the embedded catalog (parentCategories.ts), which is
 * itself zero-import: this runs inside the parse worker, which has no network
 * and so cannot ask SharePoint what a design is.
 */
import { categoryOfParent } from './parentCategories';

/** The six output series a day can be priced on. `repaired` is rework at the
 * BQC5 rate — the set went through QC again. */
export type OutputStage = 'planned' | 'cut' | 'dispatched' | 'received' | 'qc' | 'repaired';
export const OUTPUT_STAGES: readonly OutputStage[] = ['planned', 'cut', 'dispatched', 'received', 'qc', 'repaired'];

/** Which COGM-sheet column prices each stage. */
export const COGM_COLUMN: Record<OutputStage, string> = {
  planned: 'BQC1', cut: 'BQC2', dispatched: 'BQC3', received: 'BQC4', qc: 'BQC5', repaired: 'BQC5',
};

export type StageRates = Record<OutputStage, number>;

export interface ICogmCard {
  /** Parent SKU (upper-case) → per-stage rate. */
  bySku: Record<string, StageRates>;
  /** Category (upper-case) → per-stage AVERAGE over that category's rows. */
  byCategory: Record<string, StageRates>;
  /** Parent SKU → the category the COGM sheet files it under. */
  categoryOfSku: Record<string, string>;
  /** Rows read — 0 means the sheet was missing or empty, and nothing prices. */
  rows: number;
}

export const EMPTY_CARD: ICogmCard = { bySku: {}, byCategory: {}, categoryOfSku: {}, rows: 0 };

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v).replace(/\s+/g, ' ').trim());
const upper = (v: unknown): string => text(v).toUpperCase();
const numOf = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[₱,\s]/g, ''));
  return Number.isFinite(n) ? n : undefined;
};

/** A row keyed by its header text; keys are matched case- and space-insensitively. */
function cell(row: Record<string, unknown>, header: string): unknown {
  const want = upper(header).replace(/\s+/g, '');
  for (const k of Object.keys(row)) if (upper(k).replace(/\s+/g, '') === want) return row[k];
  return undefined;
}

/**
 * The rate card from the COGM sheet's rows (one object per row, keyed by
 * header). Rows with no TOP SKU, or with no numeric rate in any stage column,
 * are skipped — a totals line or a blank tail is not a SKU.
 */
export function buildCogmCard(rows: readonly Record<string, unknown>[]): ICogmCard {
  const bySku: Record<string, StageRates> = {};
  const categoryOfSku: Record<string, string> = {};
  const sums: Record<string, { n: number; total: StageRates }> = {};
  let count = 0;

  for (const row of rows) {
    const sku = upper(cell(row, 'TOP SKU') ?? cell(row, 'PARENT SKU') ?? cell(row, 'SKU'));
    if (!sku) continue;
    const rates = {} as StageRates;
    let any = false;
    for (const stage of OUTPUT_STAGES) {
      const v = numOf(cell(row, COGM_COLUMN[stage]));
      // BQC5's own column is the finished COGM; the sheet also carries it under
      // "BQC5 COGM", which is the same figure and stands in when BQC5 is blank.
      const fallback = stage === 'qc' || stage === 'repaired' ? numOf(cell(row, 'BQC5 COGM')) : undefined;
      const rate = v ?? fallback;
      if (rate !== undefined) any = true;
      rates[stage] = rate ?? 0;
    }
    if (!any) continue;
    count += 1;
    bySku[sku] = rates;
    const cat = upper(cell(row, 'CATEGORY'));
    if (cat) {
      categoryOfSku[sku] = cat;
      const s = sums[cat] ?? (sums[cat] = { n: 0, total: zeroRates() });
      s.n += 1;
      for (const stage of OUTPUT_STAGES) s.total[stage] += rates[stage];
    }
  }

  const byCategory: Record<string, StageRates> = {};
  for (const [cat, s] of Object.entries(sums)) {
    const avg = {} as StageRates;
    for (const stage of OUTPUT_STAGES) avg[stage] = round2(s.total[stage] / s.n);
    byCategory[cat] = avg;
  }
  return { bySku, byCategory, categoryOfSku, rows: count };
}

function zeroRates(): StageRates {
  return { planned: 0, cut: 0, dispatched: 0, received: 0, qc: 0, repaired: 0 };
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * The parent SKU a row is priced under: its DESIGN when the sheet names one,
 * else the head of its SKU CODE. '' when it has neither.
 */
export function parentSkuOf(design: unknown, sku: unknown): string {
  const d = upper(design);
  if (d && d !== '0') return d;
  const s = upper(sku);
  if (!s || s === '0') return '';
  return s.split('-')[0] ?? '';
}

export type RateBasis = 'sku' | 'category';

export interface IRate { rate: number; basis: RateBasis }

/**
 * The per-set rate for one stage, or undefined when neither lookup answers.
 * `category` is only consulted when the parent is not on the card.
 */
export function rateFor(
  card: ICogmCard, stage: OutputStage, parentSku: string, category: string,
): IRate | undefined {
  const bySku = card.bySku[parentSku];
  if (bySku) return { rate: bySku[stage], basis: 'sku' };
  const byCat = category ? card.byCategory[category] : undefined;
  if (byCat) return { rate: byCat[stage], basis: 'category' };
  return undefined;
}

/**
 * The category a row is costed under, tried in the order the floor described,
 * with the embedded catalog standing where the OPERATIONS Masterfile used to:
 *
 *   1. the catalog, by PARENT SKU — the reference list, and the only source
 *      that is the same for this app and for the hub's own VBA;
 *   2. the catalog, by the head of the full SKU CODE, for a row whose DESIGN
 *      cell is blank;
 *   3. what the COGM sheet itself files the parent under;
 *   4. the row's own CATEGORY cell (the caller passes BQC1's, then the
 *      DATABASE sheet's, already folded into `rowCategory`).
 *
 * Never a default. An unclassified bundle stays unclassified and is counted,
 * because a category invented here would pick a rate and read exactly like a
 * measurement.
 */
export function categoryFor(
  parentSku: string, sku: string, rowCategory: unknown, card: ICogmCard,
): string {
  const byParent = categoryOfParent(parentSku);
  if (byParent) return byParent;
  const bySkuHead = categoryOfParent(parentSkuOf('', sku));
  if (bySkuHead) return bySkuHead;
  const onCard = parentSku ? card.categoryOfSku[parentSku] : undefined;
  if (onCard) return onCard;
  return upper(rowCategory);
}

/** How the sets in an import were priced — what the peso figures rest on. */
export interface ICostingStats {
  /** Sets priced at their own parent SKU's rate. */
  skuRate: number;
  /** Sets priced at their category's average — parent not on the COGM sheet. */
  categoryAverage: number;
  /** Sets that could not be priced at all. Counted, never estimated. */
  unpriced: number;
  /** COGM rows the card was built from. */
  cogmRows: number;
  /**
   * True when those rows came from the BUILT-IN card (cogmSheetRows.ts) rather
   * than a COGM sheet inside the imported workbook.
   *
   * Optional because stored figures predate it: an import made before the
   * fallback existed has no opinion either way, and `undefined` reading as
   * "the workbook's own sheet" is right for every one of them — back then
   * there was no other source a peso could have come from.
   */
  shippedCard?: boolean;
  /** Bumped when the pricing rule changes, so stored figures can be told apart. */
  version: number;
}

export const COSTING_VERSION = 1;

export function emptyStats(cogmRows: number, shippedCard = false): ICostingStats {
  return { skuRate: 0, categoryAverage: 0, unpriced: 0, cogmRows, version: COSTING_VERSION, shippedCard };
}
